import React, { useState, useRef, useEffect } from 'react';
import './CustomSelect.scss';

export type CustomSelectOption = {
  value: string;
  label: string;
};

export type CustomSelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: CustomSelectOption[];
  className?: string;
  id?: string;
};

const CustomSelect = ({
  value,
  onChange,
  options,
  className = '',
  id,
}: CustomSelectProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayText = selectedOption ? selectedOption.label : '';

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: Event): void => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const target = e.target instanceof Node ? e.target : null;
      if (wrapRef.current && target && !wrapRef.current.contains(target)) {
        setIsOpen(false);
      }
    };
    const id = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string): void => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div
      ref={wrapRef}
      className={`custom-select-wrap ${className}`}
      id={id}
      tabIndex={0}
    >
      <div
        className={`custom-select-trigger ${isOpen ? 'is-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {displayText}
      </div>
      <div
        className={`custom-select-dropdown ${isOpen ? 'is-open' : ''}`}
        role="listbox"
      >
        {options.map((opt) => (
          <div
            key={opt.value}
            className={`custom-option ${opt.value === value ? 'is-selected' : ''}`}
            onClick={() => handleSelect(opt.value)}
            role="option"
            aria-selected={opt.value === value}
          >
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomSelect;
